import { Button } from '@/components/ui/button';
import { Chat, messagingService, User } from '@/services/messagingService';
import { supabase } from '@/utils/supabase';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Search, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GolfIcon from '../GolfIcon';
import { ChatListItem } from './ChatListItem';

type ChatListProps = {
  userId: string;
  activeChat: string | null;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  className?: string;
};


export default function ChatList({ userId, activeChat, onSelectChat, onNewChat, className }: ChatListProps) {
  const [chats, setChats] = useState<Chat[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<{ [key: string]: User }>({});
  const [userPresence, setUserPresence] = useState<Record<string, { online: boolean, lastSeen?: string }>>({});

  const { t } = useTranslation();

  // Fetch chats
  useEffect(() => {
    if (!userId) return;

    const fetchChats = async () => {
      try {
        const userChats = await messagingService.fetchChats(userId);
        setChats(userChats as Chat[]);

        // We still need to fetch initial presence data for the chat list
        // This is a one-time fetch, not a subscription
        const fetchInitialPresence = async () => {
          // Get all unique participant IDs from all chats
          const allParticipantIds = new Set<string>();
          chats.forEach(chat => {
            if (chat.participants) {
              chat.participants.forEach(participantId => {
                allParticipantIds.add(participantId);
              });
            }
          });

          // Fetch presence info for all participants
          if (allParticipantIds.size > 0) {
            const participantIds = Array.from(allParticipantIds);
            try {
              const { data } = await supabase
                .from('profiles')
                .select('userId, first_name, last_name, online, last_seen')
                .in('userId', participantIds);

              if (data) {
                const presenceData: Record<string, { online: boolean, lastSeen?: string }> = {};
                data.forEach((profile: any) => {
                  presenceData[profile.userId] = {
                    online: profile.online || false,
                    lastSeen: profile.last_seen
                  };
                });
                setUserPresence(presenceData);
              }
            } catch (err) {
              console.error('Error fetching user presence:', err);
            }
          }
        };

        if (chats.length > 0) {
          fetchInitialPresence();
        }
      } catch (error) {
        console.error('Error fetching chats:', error);
        setChats([]);
      }
    };

    fetchChats();

    // Subscribe to chat updates
    const subscription = messagingService.subscribeToChats(userId, (payload) => {
      if (payload.new) {
        // Update or add the chat
        setChats(prev => {
          const exists = prev.some(chat => chat.id === payload.new.id);
          if (exists) {
            return prev.map(chat =>
              chat.id === payload.new.id ? payload.new : chat
            );
          } else {
            return [...prev, payload.new];
          }
        });
      } else if (payload.old) {
        // Remove the chat
        setChats(prev => prev.filter(chat => chat.id !== payload.old.id));
      }
    });

    return () => {
      subscription?.unsubscribe();
    };


  }, [userId, chats.length]);

  // Fetch user profiles for chat names and avatars
  useEffect(() => {
    const fetchUserProfiles = async () => {
      try {
        // Get all unique user IDs from chats
        const userIds = new Set<string>();
        chats.forEach(chat => {
          if (chat.type === 'direct') {
            chat.participants.forEach(id => {
              if (id !== userId) {
                userIds.add(id);
              }
            });
          }
        });

        if (userIds.size === 0) return;

        // Fetch user profiles
        const userProfiles = await messagingService.fetchUserProfiles(Array.from(userIds));
        setUsers(userProfiles);
      } catch (error) {
        console.error('Error fetching user profiles:', error);
      }
    };

    fetchUserProfiles();
  }, [chats, userId]);

  // Helper function to get chat name
  const getChatName = (chat: Chat) => {
    if (chat.name) return chat.name;

    if (chat.type === 'direct' && chat.participants) {
      const otherUserId = chat.participants.find(id => id !== userId);
      if (otherUserId && users[otherUserId]) {
        return users[otherUserId].firstName + ' ' + users[otherUserId].lastName;
      }
    }

    return 'Chat';
  };

  // Filter chats based on search query
  const filteredChats = chats.filter(chat => {
    if (!searchQuery) return true;

    const chatName = getChatName(chat).toLowerCase();
    return chatName.includes(searchQuery.toLowerCase());
  });

  const getChatImage = (chat: Chat) => {
    if (chat.imageUrl) return chat.imageUrl;

    if (chat.type === 'direct' && chat.participants) {
      const otherUserId = chat.participants.find(id => id !== userId);
      if (otherUserId && users[otherUserId]) {
        return users[otherUserId].imageUrl;
      }
    }

    return undefined;
  };

  // We'll handle unread status in a useEffect for each chat

  const handleDeleteChat = async (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();

    if (window.confirm(t('confirm_delete_chat'))) {
      try {
        // Optimistically remove from UI first for better UX
        setChats(prev => prev.filter(chat => chat.id !== chatId));

        // Then delete from database
        await messagingService.deleteChat(chatId);

        // If the active chat was deleted, clear the selection
        if (activeChat === chatId) {
          onSelectChat('');
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
        // If deletion fails, restore the chat in the UI
        const fetchChats = async () => {
          try {
            const userChats = await messagingService.fetchChats(userId);
            setChats(userChats as Chat[]);
          } catch (fetchError) {
            console.error('Error restoring chats:', fetchError);
          }
        };
        fetchChats();
      }
    }
  };

  // Sort chats by last message time (most recent first)
  filteredChats.sort((a, b) => {
    const aTime = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
    const bTime = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
    return bTime - aTime;
  });

  return (
    <div className={`flex size-full flex-col ${className}`}>
      <div className="hidden md:flex items-center justify-between p-3">
        <h2 className="text-xl font-bold">{t('messages')}</h2>
        <Button variant="ghost" size="icon" onClick={onNewChat}>
          <GolfIcon name='message-add' className='size-7 dark:invert' />
        </Button>
      </div>

      <div className="p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-input bg-transparent pl-10 pr-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
      </div>

      {filteredChats.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-4 md:hidden">
          <div className="flex h-full gap-2 flex-col items-center justify-center">
            <GolfIcon name="message-multiple" className="size-32 brightness-50¨" />
            <p className="text-lg font-medium text-foreground">{t('no_messages_yet')}</p>
            <p className="text-sm text-muted-foreground">{t('start_conversation')}</p>
            <button
              onClick={onNewChat}
              className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              {t('new_chat')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {filteredChats.map((chat) => (
            <IonItemSliding key={chat.id}>
              <IonItem color="transparent" lines="none" className="rounded-none md:rounded-lg ion-no-padding pl-4">
                <ChatListItem
                  chat={{
                    id: chat.id,
                    name: getChatName(chat),
                    imageUrl: getChatImage(chat),
                    lastMessage: chat.lastMessage
                      ? {
                        text: chat.lastMessage,
                        timestamp: chat.lastMessageTime || new Date().toISOString(),
                        sender: chat.createdBy || '',
                      }
                      : undefined,
                    type: chat.type,
                    participants: chat.participants,
                  }}
                  isActive={activeChat === chat.id}
                  onClick={() => onSelectChat(chat.id)}
                  userPresence={userPresence}
                />
              </IonItem>

              <IonItemOptions side="end">
                <IonItemOption
                  color="danger"
                  onClick={(e) => handleDeleteChat(e, chat.id)}
                >
                  <div className="flex flex-col items-center h-full justify-center">
                    <Trash2 className="size-5" />
                    <span className="text-xs mt-1">{t('delete')}</span>
                  </div>
                </IonItemOption>
              </IonItemOptions>
            </IonItemSliding>
          ))}
        </>
      )}
    </div>
  );
}
